<template>
  <div v-if="canShowActions" class="flex gap-1 w-full flex-col">
    <template v-if="canBuyNowOrAddToCart">
      <template v-if="canShowBuyNow">
        <LazyBuyNow
          :key="`${product.id}_buy`"
          :product="product"
          :product-details="productDetails"
          :quantity="1"
          :custom-fields="product.item_fields"
          :selected-fields="selectedFields"
          :slug="props.product.hash"
          :component-hint="props.componentHint"
          :search-text="props.searchText"
          :query-key="props.queryKey"
          class="w-full"
          hide-modals
          @open-out-of-stock-modal="isOutOfStockVisible = true" />
      </template>
      <template v-else>
        <LazyAddToCart
          :key="`${product.id}_cart`"
          :product="product"
          :product-details="productDetails"
          :in-stock="product.in_stock"
          :quantity="1"
          :custom-fields="product.item_fields"
          :selected-fields="selectedFields"
          :slug="props.product.hash"
          :component-hint="props.componentHint"
          :search-text="props.searchText"
          :query-key="props.queryKey"
          :can-reserve="false"
          class="w-full"
          @success="onAddToCart"
          @open-out-of-stock-modal="isOutOfStockVisible = true" />
      </template>
    </template>
    <template v-else>
      <LazyViewItem
        :key="`${product.id}_view`"
        :product="product"
        :index="index"
        :query-key="queryKey"
        :search-text="searchText"
        :component-hint="componentHint"
        :redirect-url="redirectUrl" />
    </template>
  </div>
  <ClientOnly>
    <Teleport to="#modal-root">
      <ModalOutOfStock
        :is-visible="isOutOfStockVisible"
        @handle="(value) => (isOutOfStockVisible = value)"
        @on-button-click="onOutOfStockButtonClick" />
    </Teleport>
  </ClientOnly>
</template>

<script lang="ts" setup>
import type { Product } from '~/types/searchTypes'
import type { ProductDetails } from '~/types/Product'
import { InteractionPlaceComponentNames } from '~/types/analytics/InteractionPlace'
import type { AddedItemInterface } from '~/types/cartTypes'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
const { isAlt } = useTheme()

const props = defineProps<{
  product: Product
  index: number
  queryKey?: string | undefined // Used for tracking
  searchText?: string | undefined // Used for tracking,
  componentHint?: InteractionPlaceComponentNames | undefined
  redirectUrl?: string
}>()

const total = computed(() => props.product.current_price ?? 0)
const { canReserve } = useMpReservation(total)

const canShowActions = computed(() => {
  return props.product.in_stock && isAlt
})

const canShowBuyNow = computed(() => {
  return canReserve.value
})

const canBuyNowOrAddToCart = computed(() => {
  const itemFields = props.product.item_fields

  if (typeof itemFields === 'undefined') {
    return false
  }

  if (!itemFields.length) {
    return true
  }

  return itemFields.every((itemField) => {
    return itemField.values.some((itemFieldValue) => itemFieldValue.default)
  })
})

const selectedFields = computed(() => {
  const itemFields = props.product.item_fields

  if (typeof itemFields === 'undefined') {
    return {}
  }

  if (!itemFields.length) {
    return {}
  }

  const selectedFields: {
    [key: number]: {
      id: string
      key: string
      value: string
      disabled: boolean
    }[]
  } = {}

  for (const itemField of itemFields) {
    const defaultValue = itemField.values.find((itemFieldValue) => itemFieldValue.default)?.value ?? null

    if (defaultValue !== null) {
      selectedFields[itemField.id] = {
        id: JSON.stringify([itemField.id, defaultValue]),
        key: JSON.stringify([itemField.id, defaultValue]),
        value: defaultValue,
        disabled: false,
      }
    }
  }

  return selectedFields
})

const productDetails = computed<ProductDetails>(() => ({
  ...props.product,
  media: [props.product.image],
  item_id: props.product.id,
}))

const isOutOfStockVisible = ref(false)

const onOutOfStockButtonClick = async () => {
  isOutOfStockVisible.value = false

  await navigateTo(buildCategoryUrl(null))
}

onBeforeRouteLeave(() => {
  isOutOfStockVisible.value = false
})

const emit = defineEmits(['onAddToCart'])

const onAddToCart = (data: AddedItemInterface) => {
  emit('onAddToCart', data)
}
</script>
